import { useRef } from "react";
import { ArrowRight, Phone, Clock, HandshakeIcon, Building2, Home } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { MagneticButton } from "@/components/MagneticButton";
import { HeroQuickQuote } from "@/components/HeroQuickQuote";
import { AmbientBg } from "@/components/AmbientBg";
import { CursorFollower } from "@/components/CursorFollower";
import { WordRotate } from "@/components/WordRotate";
import { VimeoLoop } from "@/components/VimeoLoop";
import { ThreeMBadge } from "@/components/ThreeMBadge";


function openQuote() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("open-quote-modal"));
}

export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-svh flex items-center pt-28 pb-12 md:pb-20 overflow-hidden bg-navy"
    >
      {/* Vimeo background video with parallax */}
      <motion.div style={{ y: videoY }} className="absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 w-full h-full">
          <VimeoLoop
            videoId="249509620"
            start={3}
            end={81}
            title="BBN Nordic installation"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] h-[56.25vw] min-w-full min-h-full pointer-events-none"
          />
        </div>

        {/* Consistent dark overlay for text legibility across all video frames */}
        <div
          aria-hidden
          className="absolute inset-0 bg-black/20"
        />

        {/* Asymmetric AAA-legibility gradient — heavy on the left, transparent on the right */}
        <motion.div
          style={{ opacity: overlayOpacity }}
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/25 to-transparent"
        />
      </motion.div>


      {/* Ambient animated bg — grid + golden light rays */}
      <AmbientBg />

      {/* Golden cursor blob (desktop pointer only) */}
      <CursorFollower />

      {/* Sweeping film highlight (one-shot on load) */}
      <motion.div
        aria-hidden
        initial={{ x: "-110%", opacity: 0 }}
        animate={{ x: "120%", opacity: [0, 0.5, 0] }}
        transition={{ duration: 1.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="absolute inset-y-0 -left-1/3 w-1/3 pointer-events-none bg-gradient-to-r from-transparent via-gold/15 to-transparent skew-x-12"
      />

      <motion.div
        style={{ y: contentY }}
        className="relative mx-auto max-w-7xl px-5 md:px-10 w-full"
      >
       <div
         style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.5)" }}
       >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-12 lg:gap-16">
          {/* Content column */}
          <div className="relative z-10 lg:w-3/5 space-y-7">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2.5 border border-gold/30 bg-gold/10 px-3 py-1.5 rounded-sm">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-gold-bright opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-bright" />
                </span>
                <span className="font-mono text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-gold-bright">
                  Auktoriserad 3M-installatör · Hela Sverige
                </span>
              </span>
              <ThreeMBadge variant="compact" />
            </div>

            <h1
              className="font-display text-cream text-[2.8rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.9] tracking-tight uppercase"
            >
              <span className="block">Sänk</span>
              <span className="block overflow-hidden pt-[0.18em] -mt-[0.18em] pb-[0.18em] -mb-[0.18em]">
                <WordRotate
                  words={["värmen.", "kostnaden.", "bländningen.", "UV-strålarna."]}
                  className="text-gold-bright"
                />
              </span>
              <span className="block mt-1">Höj komforten.</span>
            </h1>

            {/* Företag / Privat segmented toggle — gör direkt synligt att vi servar båda */}
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="inline-flex items-center gap-1 p-1 border border-gold/30 bg-navy-deep/60 backdrop-blur-sm rounded-full"
              role="group"
              aria-label="Välj kundtyp"
            >
              <Link
                to="/solfilm-foretag"
                className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-semibold text-navy bg-gold hover:bg-gold-bright transition-colors"
              >
                <Building2 size={15} /> För företag
              </Link>
              <Link
                to="/solfilm-privat"
                className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-semibold text-cream/85 hover:text-gold-bright hover:bg-white/5 transition-colors"
              >
                <Home size={15} /> Solfilm privat
              </Link>
            </motion.div>


            <motion.p
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hidden md:block max-w-lg text-lg md:text-xl text-cream/80 leading-relaxed border-l-2 border-gold pl-6"
            >
              Vi hjälper dig att minska värmen i fastigheten med <span className="text-cream font-medium">3M solfilm</span>.
              Med solfilm minskar du värmeinstrålningen med <span className="text-gold-bright font-semibold">36–61 %</span> (3M Prestige-serien) och
              får <span className="text-cream font-medium">10 års garanti</span>.
            </motion.p>

            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="hidden md:flex flex-wrap items-center gap-6 pt-2"
            >
              <MagneticButton
                type="button"
                onClick={openQuote}
                className="group relative inline-flex items-center justify-center gap-3 bg-gold text-navy px-8 py-5 rounded-full font-bold tracking-[0.04em] text-sm overflow-hidden transition-all hover:pr-12 shadow-[0_10px_40px_rgba(196,151,58,0.4)]"
              >
                <span>Få gratis offert</span>
                <ArrowRight
                  size={18}
                  className="absolute right-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                />
              </MagneticButton>


              <a
                href="tel:+46771502502"
                className="group inline-flex items-center gap-2.5 border border-gold/30 bg-gold/10 px-5 py-3 rounded-sm hover:bg-gold/20 hover:border-gold/50 transition-all"
              >
                <Phone size={16} className="text-gold-bright" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright">
                  Ring
                </span>
                <span className="text-lg font-semibold text-cream group-hover:text-white transition-colors">
                  0771 502 502
                </span>
              </a>
            </motion.div>

            {/* Mobile: kompakt direktkontakt — klickbar telefonlänk */}
            <motion.a
              href="tel:+46771502502"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="md:hidden inline-flex items-center justify-center gap-2.5 border border-gold/30 bg-gold/10 px-5 py-3 rounded-sm w-full max-w-xs hover:bg-gold/20 hover:border-gold/50 transition-all"
            >
              <Phone size={18} className="text-gold-bright" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright">Ring</span>
              <span className="text-lg font-semibold text-cream">0771 502 502</span>
            </motion.a>

            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="hidden md:flex flex-wrap items-center gap-x-5 gap-y-2 -mt-2"
            >
              <span className="inline-flex items-center gap-2 font-mono text-[13px] md:text-[14px] font-bold uppercase tracking-[0.18em] text-teal-bright">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-teal-bright opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-bright" />
                </span>
                Vi hör av oss om din förfrågan
              </span>
              <span className="inline-flex items-center gap-2 font-mono text-[13px] md:text-[14px] font-bold uppercase tracking-[0.18em] text-gold-bright">
                <Clock size={15} /> Snabbt svar på vardagar
              </span>
              <span className="inline-flex items-center gap-2 font-mono text-[13px] md:text-[14px] font-semibold uppercase tracking-[0.18em] text-cream/85">
                <HandshakeIcon size={15} /> Fast pris efter mätning på plats
              </span>
            </motion.div>


            <motion.p
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="hidden md:block font-mono text-[13px] md:text-[14px] font-bold uppercase tracking-[0.18em] text-cream"
            >
              3 500+ nöjda kunder · auktoriserad 3M-installatör sedan 2008
            </motion.p>
          </div>

          {/* Quick-quote starter */}
          <HeroQuickQuote />
        </div>
       </div>
      </motion.div>
    </section>
  );
}
